import { ErrorMessage, Field, Form, Formik } from "formik";
import moment from "moment";
import * as Yup from "yup";
import { HashLoader } from "react-spinners";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Armchair, ArrowsLeftRight, CalendarBlank, PaperPlaneTilt } from "@phosphor-icons/react";
import IntlAirportSelector from "../../../components/IntlAirportSelector";
import PassengerSelector, { PassengerCounts } from "../../../components/PassengerSelector";
import useSearchInternationalOffers from "../../../hooks/useSearchInternationalOffers";
import useIntlFlightFeatureFlags from "../../../hooks/useIntlFlightFeatureFlags";
import { IntlSearchPayload } from "../../../@types/intlFlightAggregator";
import AviationAmbientBackground from "../../../components/AviationAmbientBackground";

interface FormValues {
  journeyType: "OneWay" | "Return";
  cabinClass: "economy" | "premium_economy" | "business" | "first";
  departureDate: string;
  returnDate: string;
  airportOriginCode: string;
  airportOriginLabel: string;
  airportDestinationCode: string;
  airportDestinationLabel: string;
  passengers: PassengerCounts;
}

const validationSchema = Yup.object().shape({
  departureDate: Yup.string()
    .required("Departure date is required")
    .test("is-future", "Departure date must be in the future", (value) =>
      moment(value).isSameOrAfter(moment().format("YYYY-MM-DD"))
    ),
  returnDate: Yup.string().when("journeyType", ([journeyType], schema) => {
    if (journeyType === "Return") {
      return schema
        .required("Return date is required")
        .test("is-future", "Return date must be on or after the departure date", function (value) {
          const { departureDate } = this.parent;
          if (!value || !departureDate) return false;
          return moment(value).isSameOrAfter(moment(departureDate), "day");
        });
    }
    return schema.notRequired();
  }),
  airportOriginCode: Yup.string().required("Origin airport is required"),
  airportDestinationCode: Yup.string()
    .required("Destination airport is required")
    .notOneOf([Yup.ref("airportOriginCode")], "Origin and destination cannot be the same"),
});

const journeyTypes: { title: string; key: FormValues["journeyType"] }[] = [
  { title: "One Way", key: "OneWay" },
  { title: "Return", key: "Return" },
];

const cabinClasses: { title: string; key: FormValues["cabinClass"] }[] = [
  { title: "Economy", key: "economy" },
  { title: "Premium Economy", key: "premium_economy" },
  { title: "Business", key: "business" },
  { title: "First", key: "first" },
];

const fieldClass =
  "rounded-xl border border-gray-200 bg-white pl-11 pr-4 py-3.5 w-full outline-none transition-colors duration-200 focus:border-visaro-orange focus:ring-4 focus:ring-visaro-orange/10";

const fieldLabelClass = "text-sm font-bold text-gray-700";

/**
 * International flight search — supplier-agnostic (Duffel <-> Travex, see
 * FlightSupplierManager on the backend). Separate from the Travex-only
 * ../international/SearchFlight, which is untouched.
 */
const SearchFlight = () => {
  const navigate = useNavigate();
  const { flightsEnabled, isLoadingFlags } = useIntlFlightFeatureFlags();

  const { searchOffers, searchingOffers } = useSearchInternationalOffers({
    onSuccess(data) {
      if (data.data) {
        navigate("/aviation/international/v2/results", {
          state: { searchResult: data.data },
        });
      }
    },
  });

  if (isLoadingFlags) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <HashLoader color="#EA5C29" />
      </div>
    );
  }

  if (!flightsEnabled) {
    return (
      <div className="min-h-screen bg-gray-50 p-10 flex flex-col items-center justify-center gap-4 text-center">
        <p className="text-gray-500 max-w-sm">
          International flights aren't available right now. Please check back soon.
        </p>
        <button
          type="button"
          onClick={() => navigate("/aviation")}
          className="bg-visaro-orange text-white px-6 py-3 rounded-xl font-semibold"
        >
          Back to Aviation
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      <AviationAmbientBackground />

      {searchingOffers && (
        <div className="fixed top-0 z-50 left-0 h-screen w-screen flex flex-col items-center justify-center gap-3 bg-black/20 backdrop-blur-sm">
          <HashLoader color="#EA5C29" />
          <span className="text-white font-medium text-sm">Searching flights…</span>
        </div>
      )}

      <Formik<FormValues>
        validationSchema={validationSchema}
        initialValues={{
          journeyType: "OneWay",
          cabinClass: "economy",
          departureDate: moment().format("YYYY-MM-DD"),
          returnDate: moment().format("YYYY-MM-DD"),
          airportOriginCode: "",
          airportOriginLabel: "",
          airportDestinationCode: "",
          airportDestinationLabel: "",
          passengers: { adults: 1, children: 0, infants: 0 },
        }}
        onSubmit={(values) => {
          const slices: IntlSearchPayload["slices"] = [
            {
              origin: values.airportOriginCode,
              destination: values.airportDestinationCode,
              departure_date: values.departureDate,
            },
          ];

          if (values.journeyType === "Return") {
            slices.push({
              origin: values.airportDestinationCode,
              destination: values.airportOriginCode,
              departure_date: values.returnDate,
            });
          }

          const passengers: IntlSearchPayload["passengers"] = [
            ...Array(values.passengers.adults).fill({ type: "adult" }),
            ...Array(values.passengers.children).fill({ type: "child" }),
            ...Array(values.passengers.infants).fill({ type: "infant_without_seat" }),
          ];

          searchOffers({
            slices,
            passengers,
            cabin_class: values.cabinClass,
          });
        }}
      >
        {({ setFieldValue, values }) => (
          <div className="relative p-5 lg:p-10 py-10 flex flex-col items-center max-w-3xl mx-auto w-full gap-6">
            {/* Hero */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-deepBlue via-deepBlue to-[#2c2c56] px-6 py-8 lg:px-10 lg:py-10 w-full">
              <div className="absolute -top-24 -right-16 size-72 rounded-full bg-visaro-orange/20 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-16 -left-10 size-56 rounded-full bg-white/5 blur-3xl pointer-events-none" />
              <PaperPlaneTilt
                size={120}
                weight="thin"
                className="hidden sm:block absolute -bottom-6 -right-4 text-white/[0.06] rotate-[18deg]"
              />

              <div className="relative flex flex-col gap-1.5">
                <span className="text-[11px] font-bold uppercase tracking-widest text-white/50">
                  International Flights
                </span>
                <h1 className="text-3xl font-bold text-white">Book International Flights</h1>
                <p className="text-sm font-medium text-white/60">
                  Search real-time fares from global airlines, priced in NGN.
                </p>
              </div>
            </div>

            {/* Form card */}
            <Form className="relative bg-white rounded-2xl shadow-[0_4px_20px_-4px_rgba(15,23,42,0.10)] border border-gray-100 p-5 lg:p-7 w-full flex flex-col gap-6">
              <div className="inline-flex bg-gray-100 rounded-xl p-1 self-start">
                {journeyTypes.map((journeyType) => (
                  <button
                    type="button"
                    onClick={() => setFieldValue("journeyType", journeyType.key)}
                    key={journeyType.key}
                    className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 ${
                      values.journeyType === journeyType.key
                        ? "bg-white text-visaro-orange shadow-sm"
                        : "bg-transparent text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {journeyType.title}
                  </button>
                ))}
              </div>

              <div className="relative grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2 w-full">
                  <label htmlFor="airportOriginCode" className="text-sm font-bold text-gray-700">
                    Departure
                  </label>
                  <IntlAirportSelector
                    onSelect={(place) => {
                      setFieldValue("airportOriginCode", place.code);
                      setFieldValue("airportOriginLabel", `${place.code} - ${place.city ?? place.name ?? ""}`);
                    }}
                    value={values.airportOriginLabel}
                    placeholder="Enter Departure City"
                  />
                  <ErrorMessage name="airportOriginCode" component="div" className="text-red-500 text-xs font-medium" />
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const {
                      airportOriginCode,
                      airportOriginLabel,
                      airportDestinationCode,
                      airportDestinationLabel,
                    } = values;
                    setFieldValue("airportOriginCode", airportDestinationCode);
                    setFieldValue("airportOriginLabel", airportDestinationLabel);
                    setFieldValue("airportDestinationCode", airportOriginCode);
                    setFieldValue("airportDestinationLabel", airportOriginLabel);
                  }}
                  aria-label="Swap origin and destination"
                  className="hidden md:flex items-center justify-center absolute left-1/2 top-9 -translate-x-1/2 z-10 size-9 rounded-full bg-white border border-gray-200 text-gray-400 shadow-sm hover:border-visaro-orange hover:text-visaro-orange hover:rotate-180 transition-all duration-300"
                >
                  <ArrowsLeftRight size={16} weight="bold" />
                </button>

                <div className="flex flex-col gap-2 w-full">
                  <label htmlFor="airportDestinationCode" className="text-sm font-bold text-gray-700">
                    Destination
                  </label>
                  <IntlAirportSelector
                    onSelect={(place) => {
                      setFieldValue("airportDestinationCode", place.code);
                      setFieldValue("airportDestinationLabel", `${place.code} - ${place.city ?? place.name ?? ""}`);
                    }}
                    value={values.airportDestinationLabel}
                    placeholder="Enter Destination City"
                    isDepature={false}
                  />
                  <ErrorMessage name="airportDestinationCode" component="div" className="text-red-500 text-xs font-medium" />
                </div>
              </div>

              <div
                className={`grid gap-5 ${values.journeyType === "Return" ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}
              >
                <div className="flex flex-col gap-2 w-full">
                  <label htmlFor="departureDate" className={fieldLabelClass}>
                    Departure Date
                  </label>
                  <div className="relative">
                    <CalendarBlank
                      size={18}
                      weight="duotone"
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                    />
                    <Field
                      type="date"
                      id="departureDate"
                      name="departureDate"
                      min={moment().format("YYYY-MM-DD")}
                      className={fieldClass}
                    />
                  </div>
                  <ErrorMessage name="departureDate" component="div" className="text-red-500 text-xs font-medium" />
                </div>

                <AnimatePresence>
                  {values.journeyType === "Return" && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="flex flex-col gap-2 w-full"
                    >
                      <label htmlFor="returnDate" className={fieldLabelClass}>
                        Return Date
                      </label>
                      <div className="relative">
                        <CalendarBlank
                          size={18}
                          weight="duotone"
                          className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                        />
                        <Field
                          type="date"
                          id="returnDate"
                          name="returnDate"
                          min={values.departureDate}
                          className={fieldClass}
                        />
                      </div>
                      <ErrorMessage name="returnDate" component="div" className="text-red-500 text-xs font-medium" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="flex flex-col gap-2 w-full">
                <label htmlFor="cabinClass" className={fieldLabelClass}>
                  Cabin Class
                </label>
                <div className="relative">
                  <Armchair
                    size={18}
                    weight="duotone"
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <Field as="select" name="cabinClass" id="cabinClass" className={`${fieldClass} appearance-none`}>
                    {cabinClasses.map((cls) => (
                      <option key={cls.key} value={cls.key}>
                        {cls.title}
                      </option>
                    ))}
                  </Field>
                </div>
              </div>

              <div className="flex flex-col gap-2 w-full">
                <PassengerSelector
                  value={values.passengers}
                  onChange={(passengers) => setFieldValue("passengers", passengers)}
                />
              </div>

              <button
                type="submit"
                disabled={searchingOffers}
                className="bg-visaro-orange text-white px-6 py-3.5 rounded-xl font-semibold hover:bg-visaro-orange/90 disabled:opacity-50 disabled:hover:bg-visaro-orange transition-colors duration-200 mt-2"
              >
                {searchingOffers ? "Searching..." : "Search Flights"}
              </button>
            </Form>
          </div>
        )}
      </Formik>
    </div>
  );
};

export default SearchFlight;
